import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getTenant } from '../config/tenants';
import { 
  LayoutDashboard, 
  Trophy, 
  MessageSquare, 
  BarChart3, 
  HelpCircle,
  LogOut,
  Settings,
  Bot,
  Plug,
  Users,
  TrendingUp,
  ShoppingBag,
  Headphones,
  ExternalLink,
  X,
  CalendarCheck,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CLIENT DASHBOARD - Full functionality for Arena, ESL, etc.
export function ClientDashboard() {
  const navigate = useNavigate();
  const tenant = getTenant(localStorage.getItem('client_tenant') || 'arena');
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', country: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('client_authenticated')) {
      navigate('/arena/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/`);
      setTournaments(data);
    } catch (e) {
      console.error('Failed to fetch:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_authenticated');
    localStorage.removeItem('client_tenant');
    navigate('/arena/login');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads`, leadForm);
      setLeadSuccess(true);
      setTimeout(() => {
        setShowLeadForm(false);
        setLeadSuccess(false);
        setLeadForm({ name: '', email: '', phone: '', country: '' });
      }, 2000);
    } catch (err) {
      console.error('Lead submit error:', err);
    } finally {
      setLeadSubmitting(false);
    }
  };

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'tournaments', icon: Trophy, label: 'Tournaments' },
    { id: 'sales', icon: ShoppingBag, label: 'Sales' },
    { id: 'support', icon: Headphones, label: 'Customer Support' },
    { id: 'integrations', icon: Plug, label: 'Integrations' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  const quickStats = [
    { icon: Trophy, label: 'Active Tournaments', value: tournaments.length || '8', color: tenant.colors.primary },
    { icon: Users, label: 'Total Users', value: '961+', color: tenant.colors.secondary },
    { icon: ShoppingBag, label: 'Sales Conversions', value: '147', color: '#10B981' },
    { icon: Headphones, label: 'Support Resolved', value: '94%', color: '#F59E0B' },
  ];

  // Sales data
  const salesData = [
    { user: 'Ahmed K.', action: 'Premium Upgrade', value: '$29.99', time: '2 min ago', status: 'completed' },
    { user: 'Sara M.', action: 'Tournament Entry', value: '$15.00', time: '15 min ago', status: 'completed' },
    { user: 'Mike R.', action: 'Bundle Purchase', value: '$49.99', time: '1 hour ago', status: 'pending' },
    { user: 'Lisa T.', action: 'Premium Upgrade', value: '$29.99', time: '2 hours ago', status: 'completed' },
  ];

  // Support tickets
  const supportTickets = [
    { id: '#1234', user: 'Player_X', issue: 'Cannot join tournament', status: 'resolved', time: '5 min ago' },
    { id: '#1233', user: 'GamerPro', issue: 'Payment not processed', status: 'in_progress', time: '20 min ago' },
    { id: '#1232', user: 'NewUser99', issue: 'Account verification', status: 'resolved', time: '1 hour ago' },
    { id: '#1231', user: 'Champion1', issue: 'Prize not received', status: 'resolved', time: '3 hours ago' },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: tenant.colors.background }}>
      {/* Sidebar */}
      <aside 
        className="w-64 border-r flex flex-col"
        style={{ background: tenant.colors.surface, borderColor: tenant.colors.border }}
      >
        <div className="p-4 border-b" style={{ borderColor: tenant.colors.border }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/10 p-1">
              <img src={tenant.logo} alt={tenant.name} className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-bold text-lg" style={{ color: tenant.colors.text }}>{tenant.agentName}</h1>
              <p className="text-xs" style={{ color: tenant.colors.textMuted }}>Admin Dashboard</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left"
                style={{ 
                  background: isActive ? `${tenant.colors.primary}20` : 'transparent',
                  color: isActive ? tenant.colors.primary : tenant.colors.textMuted,
                }}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: tenant.colors.border }}>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
            style={{ color: tenant.colors.textMuted }}
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header 
          className="px-6 py-4 border-b"
          style={{ background: tenant.colors.surface, borderColor: tenant.colors.border }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ color: tenant.colors.text }}>
                {navItems.find(n => n.id === activeTab)?.label}
              </h1>
              <p className="text-sm" style={{ color: tenant.colors.textMuted }}>
                {tenant.name} - {tenant.agentName}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <a href="/arena/chat" target="_blank" rel="noopener noreferrer">
                <Button style={{ background: tenant.colors.primary, color: '#000' }} className="font-bold" data-testid="view-customer-chat-btn">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Customer Chat
                </Button>
              </a>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {quickStats.map((stat, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg"
                    style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ background: `${stat.color}20` }}
                    >
                      <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: tenant.colors.text }}>{stat.value}</p>
                    <p className="text-sm" style={{ color: tenant.colors.textMuted }}>{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-3 gap-6">
                {/* Tournaments */}
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold" style={{ color: tenant.colors.text }}>Active Tournaments</h3>
                    <Trophy className="w-5 h-5" style={{ color: tenant.colors.primary }} />
                  </div>
                  <div className="space-y-3">
                    {tournaments.slice(0, 3).map((t, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                        <img src={t.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: tenant.colors.text }}>{t.name}</p>
                          <p className="text-xs" style={{ color: tenant.colors.textMuted }}>{t.game}</p>
                        </div>
                        <span className="text-xs" style={{ color: tenant.colors.primary }}>{t.participants}/{t.max_participants}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sales */}
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold" style={{ color: tenant.colors.text }}>Recent Sales</h3>
                    <ShoppingBag className="w-5 h-5" style={{ color: '#10B981' }} />
                  </div>
                  <div className="space-y-3">
                    {salesData.slice(0, 3).map((sale, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                        <div>
                          <p className="text-sm font-medium" style={{ color: tenant.colors.text }}>{sale.user}</p>
                          <p className="text-xs" style={{ color: tenant.colors.textMuted }}>{sale.action}</p>
                        </div>
                        <span className="text-sm font-bold" style={{ color: '#10B981' }}>{sale.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Support */}
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold" style={{ color: tenant.colors.text }}>Support Tickets</h3>
                    <Headphones className="w-5 h-5" style={{ color: '#F59E0B' }} />
                  </div>
                  <div className="space-y-3">
                    {supportTickets.slice(0, 3).map((ticket, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                        <div>
                          <p className="text-sm font-medium" style={{ color: tenant.colors.text }}>{ticket.user}</p>
                          <p className="text-xs truncate" style={{ color: tenant.colors.textMuted, maxWidth: '150px' }}>{ticket.issue}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${ticket.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {ticket.status === 'resolved' ? 'Resolved' : 'In Progress'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI Agent Status */}
              <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                <h3 className="font-bold mb-4" style={{ color: tenant.colors.text }}>AI Agent Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                      <span className="text-sm" style={{ color: tenant.colors.textMuted }}>Status</span>
                    </div>
                    <p className="font-bold text-green-400">Online</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                    <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Response Time</p>
                    <p className="font-bold" style={{ color: tenant.colors.text }}>~2.3s</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                    <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Today's Chats</p>
                    <p className="font-bold" style={{ color: tenant.colors.text }}>142</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                    <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Satisfaction</p>
                    <p className="font-bold" style={{ color: '#10B981' }}>94%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tournaments Tab */}
          {activeTab === 'tournaments' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <p style={{ color: tenant.colors.textMuted }}>{tournaments.length} tournaments</p>
                <Button style={{ background: tenant.colors.primary, color: '#000' }}>+ Add Tournament</Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tournaments.map((t, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                    <img src={t.image_url} alt="" className="w-full h-32 object-cover" />
                    <div className="p-4">
                      <h3 className="font-bold truncate" style={{ color: tenant.colors.text }}>{t.name}</h3>
                      <p className="text-sm mb-2" style={{ color: tenant.colors.textMuted }}>{t.game} • {t.date}</p>
                      <div className="flex justify-between items-center">
                        <span style={{ color: tenant.colors.primary }}>{t.prize_pool} pts</span>
                        <span className="text-sm" style={{ color: tenant.colors.textMuted }}>{t.participants}/{t.max_participants}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sales Tab */}
          {activeTab === 'sales' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Today's Revenue</p>
                  <p className="text-3xl font-bold" style={{ color: '#10B981' }}>$1,247</p>
                  <p className="text-sm text-green-400">+18% from yesterday</p>
                </div>
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Conversions</p>
                  <p className="text-3xl font-bold" style={{ color: tenant.colors.text }}>47</p>
                  <p className="text-sm text-green-400">+12% conversion rate</p>
                </div>
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Avg Order Value</p>
                  <p className="text-3xl font-bold" style={{ color: tenant.colors.text }}>$26.53</p>
                  <p className="text-sm" style={{ color: tenant.colors.textMuted }}>Across all products</p>
                </div>
              </div>
              <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                <h3 className="font-bold mb-4" style={{ color: tenant.colors.text }}>Recent Sales (AI-Assisted)</h3>
                <div className="space-y-3">
                  {salesData.map((sale, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${tenant.colors.primary}20` }}>
                          <span style={{ color: tenant.colors.primary }}>{sale.user.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: tenant.colors.text }}>{sale.user}</p>
                          <p className="text-xs" style={{ color: tenant.colors.textMuted }}>{sale.action} • {sale.time}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold" style={{ color: '#10B981' }}>{sale.value}</p>
                        <span className={`text-xs ${sale.status === 'completed' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {sale.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Open Tickets</p>
                  <p className="text-3xl font-bold" style={{ color: '#F59E0B' }}>12</p>
                  <p className="text-sm" style={{ color: tenant.colors.textMuted }}>5 high priority</p>
                </div>
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Resolved Today</p>
                  <p className="text-3xl font-bold" style={{ color: '#10B981' }}>38</p>
                  <p className="text-sm text-green-400">94% by AI</p>
                </div>
                <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                  <p className="text-sm mb-1" style={{ color: tenant.colors.textMuted }}>Avg Resolution</p>
                  <p className="text-3xl font-bold" style={{ color: tenant.colors.text }}>4.2 min</p>
                  <p className="text-sm text-green-400">-2 min from last week</p>
                </div>
              </div>
              <div className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                <h3 className="font-bold mb-4" style={{ color: tenant.colors.text }}>Support Tickets (AI-Managed)</h3>
                <div className="space-y-3">
                  {supportTickets.map((ticket, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover }}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono" style={{ color: tenant.colors.textMuted }}>{ticket.id}</span>
                        <div>
                          <p className="font-medium" style={{ color: tenant.colors.text }}>{ticket.user}</p>
                          <p className="text-xs" style={{ color: tenant.colors.textMuted }}>{ticket.issue}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${ticket.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {ticket.status === 'resolved' ? 'Resolved by AI' : 'In Progress'}
                        </span>
                        <p className="text-xs mt-1" style={{ color: tenant.colors.textMuted }}>{ticket.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <p className="text-sm" style={{ color: tenant.colors.textMuted }}>
                Connect your AI agent to different channels and data sources
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'Telegram Bot', desc: 'Deploy as Telegram bot', color: '#0088cc', status: 'available' },
                  { name: 'WhatsApp', desc: 'WhatsApp Business API', color: '#25D366', status: 'available' },
                  { name: 'Google Sheets', desc: 'Sync tournament data', color: '#34A853', status: 'available' },
                  { name: 'Excel / XLS', desc: 'Import from Excel', color: '#217346', status: 'available' },
                  { name: 'Website Widget', desc: 'Embed on website', color: tenant.colors.primary, status: 'connected' },
                ].map((int, idx) => (
                  <div key={idx} className="rounded-lg p-5" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${int.color}20` }}>
                        <Plug className="w-5 h-5" style={{ color: int.color }} />
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${int.status === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {int.status === 'connected' ? 'Connected' : 'Available'}
                      </span>
                    </div>
                    <h3 className="font-bold" style={{ color: tenant.colors.text }}>{int.name}</h3>
                    <p className="text-sm mb-3" style={{ color: tenant.colors.textMuted }}>{int.desc}</p>
                    <Button className="w-full" style={{ background: int.status === 'connected' ? tenant.colors.surfaceHover : int.color, color: int.status === 'connected' ? tenant.colors.text : '#fff' }}>
                      {int.status === 'connected' ? 'Configure' : 'Connect'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="rounded-lg p-6" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
                <h3 className="font-bold mb-4" style={{ color: tenant.colors.text }}>Agent Settings</h3>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: tenant.colors.textMuted }}>Agent Name</label>
                    <input type="text" defaultValue={tenant.agentName} className="w-full p-3 rounded-lg" style={{ background: tenant.colors.surfaceHover, color: tenant.colors.text, border: `1px solid ${tenant.colors.border}` }} />
                  </div>
                  <div>
                    <label className="block text-sm mb-2" style={{ color: tenant.colors.textMuted }}>Welcome Message</label>
                    <textarea defaultValue={tenant.welcomeMessage} rows={3} className="w-full p-3 rounded-lg resize-none" style={{ background: tenant.colors.surfaceHover, color: tenant.colors.text, border: `1px solid ${tenant.colors.border}` }} />
                  </div>
                  <Button style={{ background: tenant.colors.primary, color: '#000' }}>Save Changes</Button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Book Demo Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="lead-form-modal">
          <div className="w-full max-w-md mx-4 rounded-xl p-6" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-bold text-xl" style={{ color: tenant.colors.text }}>Book a Demo</h2>
                <p className="text-sm" style={{ color: tenant.colors.textMuted }}>Get a personalized walkthrough of XOClaw</p>
              </div>
              <button onClick={() => { setShowLeadForm(false); setLeadSuccess(false); }} className="p-1 rounded-lg hover:bg-white/10" data-testid="lead-form-close">
                <X className="w-5 h-5" style={{ color: tenant.colors.textMuted }} />
              </button>
            </div>

            {leadSuccess ? (
              <div className="text-center py-8" data-testid="lead-form-success">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${tenant.colors.primary}20` }}>
                  <CalendarCheck className="w-8 h-8" style={{ color: tenant.colors.primary }} />
                </div>
                <h3 className="font-bold text-lg mb-1" style={{ color: tenant.colors.text }}>Demo Booked!</h3>
                <p className="text-sm" style={{ color: tenant.colors.textMuted }}>We'll reach out to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label style={{ color: tenant.colors.text }}>Full Name</Label>
                  <Input
                    value={leadForm.name}
                    onChange={(e) => setLeadForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="John Doe"
                    required
                    className="border text-white placeholder:opacity-50"
                    style={{ background: tenant.colors.surfaceHover, borderColor: tenant.colors.border }}
                    data-testid="lead-form-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label style={{ color: tenant.colors.text }}>Email</Label>
                  <Input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="john@company.com"
                    required
                    className="border text-white placeholder:opacity-50"
                    style={{ background: tenant.colors.surfaceHover, borderColor: tenant.colors.border }}
                    data-testid="lead-form-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label style={{ color: tenant.colors.text }}>Phone</Label>
                  <Input
                    type="tel"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+971 50 123 4567"
                    required
                    className="border text-white placeholder:opacity-50"
                    style={{ background: tenant.colors.surfaceHover, borderColor: tenant.colors.border }}
                    data-testid="lead-form-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label style={{ color: tenant.colors.text }}>Country</Label>
                  <Input
                    value={leadForm.country}
                    onChange={(e) => setLeadForm(p => ({ ...p, country: e.target.value }))}
                    placeholder="UAE"
                    required
                    className="border text-white placeholder:opacity-50"
                    style={{ background: tenant.colors.surfaceHover, borderColor: tenant.colors.border }}
                    data-testid="lead-form-country"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full font-bold mt-2" 
                  style={{ background: tenant.colors.primary, color: '#000' }}
                  disabled={leadSubmitting}
                  data-testid="lead-form-submit"
                >
                  {leadSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
